var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { PerformanceChartGroup, partSecuredColor, energySavingColor, } from "./types";
import { systemDataConstants } from "api/system-data";
import { airConsumptionYAxisKey, liftingForcesYAxisHorizontalKey, liftingForcesYAxisVerticalKey, PAD_SECONDS, robotMotionYAxisKey, suctionCupIdPrefix, vacuumPumpIdPrefix, xAxisKey, } from "ui/components/panels/graphs/constants";
import { SystemPerformanceNodeType, } from "api/types";
import { Unit } from "utility/number";
import { getVacuumPumps, getSuctionCups, } from "ui/components/panels/graphs/common/helper-functions";
export var convertVacuumLevelToFixedNumber = function (vacuumLevel) {
    return Number(vacuumLevel.toFixed(4)) * -1;
};
export var convertRobotMotionToFixedNumber = function (robotMotionValue) {
    return Number(robotMotionValue.toFixed(4));
};
// The unit we get from the API is NL/s, we convert it to minutes by multiplying by 60.
export var convertAirConsumptionToFixedNumber = function (airConsumptionValue) { return Number(airConsumptionValue.toFixed(4)) * 60; };
export var convertLiftingForceToFixedNumber = function (liftingForceValue) {
    return Number(liftingForceValue.toFixed(4));
};
export function generateVacuumLevelsWithUniqueIds(nodes, i, nodeType, idPrefix) {
    var min = Number.MAX_SAFE_INTEGER;
    var max = Number.MIN_SAFE_INTEGER;
    var values = nodes.reduce(function (acc, node) {
        var prefixedNodeId = idPrefix + node.id;
        var vacuumLevels = node.performanceData.vacuumLevels;
        if (!vacuumLevels) {
            console.error("Missing vacuumlevels for ".concat(nodeType, "-ID: "), node.id);
            return acc;
        }
        var vacuumLevel = convertVacuumLevelToFixedNumber(vacuumLevels[i]);
        min = Math.min(min, vacuumLevel);
        max = Math.max(max, vacuumLevel);
        acc[prefixedNodeId] = vacuumLevel;
        return acc;
    }, {});
    return { values: values, domainUpdate: [min, max] };
}
export function generateLiftingForcesWithUniqueIds(nodes, i) {
    var min = Number.MAX_SAFE_INTEGER;
    var max = Number.MIN_SAFE_INTEGER;
    var values = nodes.reduce(function (acc, node) {
        var verticalForcePrefixedId = liftingForcesYAxisVerticalKey + "-" + node.id;
        var verticalForces = node.performanceData.liftingForceVertical;
        var horizontalForcePrefixedId = liftingForcesYAxisHorizontalKey + "-" + node.id;
        var horizontalForces = node.performanceData.liftingForceHorizontal;
        if (!verticalForces || !horizontalForces) {
            console.error("Missing lifting forces for: ", node.id);
            return acc;
        }
        var verticalForce = convertLiftingForceToFixedNumber(verticalForces[i]);
        var horizontalForce = convertLiftingForceToFixedNumber(horizontalForces[i]);
        min = Math.min(min, Math.min(verticalForce, horizontalForce));
        max = Math.max(max, Math.max(verticalForce, horizontalForce));
        acc[verticalForcePrefixedId] = verticalForce;
        acc[horizontalForcePrefixedId] = horizontalForce;
        return acc;
    }, {});
    return { values: values, domainUpdate: [min, max] };
}
export function parseGraphDataForRecharts(graphData) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var vacuumPumps = graphData.vacuumPumps, suctionCups = graphData.suctionCups, systemProperty = graphData.systemProperty;
    var robotMotionVsTime = systemProperty.robotMotionVsTime, airConsumptionVsTime = systemProperty.airConsumptionVsTime, timeData = systemProperty.timeData, psIndex = systemProperty.psIndex;
    if (!vacuumPumps.length)
        return;
    var robotMotionData = [];
    var airConsumptionData = [];
    var nodeData = [];
    var liftingForcesData = [];
    if (robotMotionVsTime.length !== airConsumptionVsTime.length ||
        airConsumptionVsTime.length !== timeData.length) {
        console.error("Arrays from API not the same length");
        return;
    }
    var initialDomain = [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];
    var vacuumLevelDomain = __spreadArray([], __read(initialDomain), false);
    var airConsumptionDomain = __spreadArray([], __read(initialDomain), false);
    var robotMotionDomain = __spreadArray([], __read(initialDomain), false);
    var liftingForcesDomain = __spreadArray([], __read(initialDomain), false);
    for (var i = 0; i < timeData.length; i++) {
        var pumpsResult = generateVacuumLevelsWithUniqueIds(vacuumPumps, i, SystemPerformanceNodeType.VACUUM_PUMP, vacuumPumpIdPrefix);
        var cupsResult = generateVacuumLevelsWithUniqueIds(suctionCups, i, SystemPerformanceNodeType.SUCTION_CUP, suctionCupIdPrefix);
        vacuumLevelDomain = [
            Math.min(vacuumLevelDomain[0], pumpsResult.domainUpdate[0], cupsResult.domainUpdate[0]),
            Math.max(vacuumLevelDomain[1], pumpsResult.domainUpdate[1], cupsResult.domainUpdate[1]),
        ];
        var robotMotion = convertRobotMotionToFixedNumber(robotMotionVsTime[i]);
        robotMotionDomain = [
            Math.min(robotMotionDomain[0], robotMotion),
            Math.max(robotMotionDomain[1], robotMotion),
        ];
        var airConsumption = convertAirConsumptionToFixedNumber(airConsumptionVsTime[i]);
        /* The first few milliseconds, air consumption will reach very high values.
         * we need to make sure that we don't calculate the values of the domain until after part secured index to avoid that.
         */
        if (i >= psIndex) {
            airConsumptionDomain = [
                Math.min(airConsumptionDomain[0], airConsumption),
                Math.max(airConsumptionDomain[1], airConsumption),
            ];
        }
        var liftResult = generateLiftingForcesWithUniqueIds(suctionCups, i);
        liftingForcesDomain = [
            Math.min(liftingForcesDomain[0], liftResult.domainUpdate[0]),
            Math.max(liftingForcesDomain[1], liftResult.domainUpdate[1]),
        ];
        robotMotionData.push((_a = {},
            _a[xAxisKey] = timeData[i],
            _a[robotMotionYAxisKey] = robotMotion,
            _a));
        airConsumptionData.push((_b = {},
            _b[xAxisKey] = timeData[i],
            _b[airConsumptionYAxisKey] = airConsumption,
            _b));
        nodeData.push(__assign(__assign((_c = {}, _c[xAxisKey] = timeData[i], _c), pumpsResult.values), cupsResult.values));
        liftingForcesData.push(__assign((_d = {}, _d[xAxisKey] = timeData[i], _d), liftResult.values));
    }
    var padTime = timeData[0] - PAD_SECONDS;
    nodeData.unshift(__assign(__assign({}, nodeData[0]), (_e = {}, _e[xAxisKey] = padTime, _e)));
    airConsumptionData.unshift(__assign(__assign({}, airConsumptionData[0]), (_f = {}, _f[xAxisKey] = padTime, _f)));
    robotMotionData.unshift(__assign(__assign({}, robotMotionData[0]), (_g = {}, _g[xAxisKey] = padTime, _g)));
    liftingForcesData.unshift(__assign(__assign({}, liftingForcesData[0]), (_h = {}, _h[xAxisKey] = padTime, _h)));
    return {
        nodeData: nodeData,
        airConsumptionData: airConsumptionData,
        robotMotionData: robotMotionData,
        vacuumLevelDomain: vacuumLevelDomain,
        airConsumptionDomain: airConsumptionDomain,
        robotMotionDomain: robotMotionDomain,
        liftingForcesDomain: liftingForcesDomain,
        liftingForcesData: liftingForcesData,
    };
}
export function buildDashboardData(activePerformanceData, systemDataSnapshot) {
    var parameterNames = systemDataConstants.parameterNames;
    var getParameterValue = function (parameterName) { var _a; return Number((_a = systemDataSnapshot.parameters[parameterName]) === null || _a === void 0 ? void 0 : _a.value) || 0; };
    var timeToPartSecured = activePerformanceData.systemTimeToPartSecured;
    // The API's scalar `transferTimeWithPart` can disagree with the real
    // part-secured → place span of the sampled motion curve by a few hundredths
    // of a second (place_start lands slightly later than ps + transfer_time).
    // Derive the transfer-with-part duration from the curve (place_start time −
    // part-secured time) when a valid place-start index is available, so the
    // printed dashboard numbers and the SVG timeline (which sums these values)
    // both line up with where the recharts graphs show the robot placing the
    // part. Defining it relative to `timeToPartSecured` makes the timeline's
    // `timeToPartSecured + transferToPlace` compose exactly onto place_start.
    // Fall back to the API scalar when the curve isn't available.
    var placeStartIndex = activePerformanceData.placeStartIndex;
    var placeStartTime = placeStartIndex > 0
        ? activePerformanceData.vacuumLevelTimeData[placeStartIndex]
        : undefined;
    var transferTimeWithPart = typeof placeStartTime === "number" && placeStartTime > timeToPartSecured
        ? placeStartTime - timeToPartSecured
        : activePerformanceData.transferTimeWithPart;
    var timeToEnergySave = activePerformanceData.systemTimeToEnergySave > 0
        ? activePerformanceData.systemTimeToEnergySave
        : undefined;
    var targetPartsPerMinute = {
        value: getParameterValue(parameterNames.targetPartsPerMinute) || 1,
        translationKey: "dashboard.parts_per_minute",
    };
    return {
        applicationSpeed: __assign(__assign(__assign(__assign({ vacuumOnBeforePick: {
                id: "vacuum_on_before_pick",
                value: getParameterValue(parameterNames.vacuumOnBeforePick),
                translationKey: "dashboard.vacuum_on_before_pick",
                color: "#56CCF2",
                borderColor: "#56CCF2",
                parameterId: parameterNames.vacuumOnBeforePick,
                group: PerformanceChartGroup.BEFORE_PICK,
            }, blowOffOnBeforePick: {
                id: "blow_off_on_before_pick",
                value: getParameterValue(parameterNames.blowOffOnBeforePick),
                translationKey: "dashboard.blow_off_on_before_pick",
                color: "#F04E35",
                borderColor: "#F04E35",
                parameterId: parameterNames.blowOffOnBeforePick,
                group: PerformanceChartGroup.BEFORE_PICK,
            }, 
            /* PICK */
            timeToPartSecured: {
                id: "time_to_part_secured",
                value: timeToPartSecured,
                translationKey: "dashboard.time_to_part_secured",
                descriptionKey: "descriptions.dashboard.time_to_part_secured",
                color: "#2F80ED",
                borderColor: "#2F80ED",
                group: PerformanceChartGroup.BEFORE_PLACE,
            } }, (timeToEnergySave && {
            delayToEnergySave: {
                id: "delay_to_es",
                value: timeToEnergySave - timeToPartSecured,
                translationKey: "dashboard.delay_to_es",
                descriptionKey: "descriptions.dashboard.delay_to_es",
                color: "#C4C4C4",
                borderColor: "#2F80ED",
                group: PerformanceChartGroup.BEFORE_PLACE,
            },
        })), (timeToEnergySave && {
            timeWithEnergySavingOn: {
                id: "es_on_tooltip",
                value: Math.max(0, transferTimeWithPart - timeToEnergySave + timeToPartSecured),
                translationKey: "dashboard.es_on_tooltip",
                descriptionKey: "descriptions.dashboard.es_on_tooltip",
                color: energySavingColor,
                borderColor: energySavingColor,
                group: PerformanceChartGroup.BEFORE_PLACE,
            },
        })), (!timeToEnergySave && {
            timeWithEnergySavingOff: {
                id: "es_off_tooltip",
                value: transferTimeWithPart,
                translationKey: "dashboard.es_off_tooltip",
                descriptionKey: "descriptions.dashboard.es_off_tooltip",
                color: "#F2C94C",
                borderColor: "#F2C94C",
                group: PerformanceChartGroup.BEFORE_PLACE,
            },
        })), { transferToPlace: {
                id: "transfer_to_place",
                value: transferTimeWithPart,
                translationKey: "dashboard.transfer_to_place",
                descriptionKey: "descriptions.dashboard.transfer_to_place",
                color: "#C4C4C4",
                borderColor: "#C4C4C4",
                group: PerformanceChartGroup.BEFORE_PLACE,
            }, 
            /* PLACE */
            releaseTimeToPlacePart: {
                id: "release_time_to_place_part",
                value: activePerformanceData.blowOffTimeToAtm,
                translationKey: "dashboard.release_time_to_place_part",
                descriptionKey: "descriptions.dashboard.release_time_to_place_part",
                color: "#FB7F7F",
                borderColor: "#FB7F7F",
                group: PerformanceChartGroup.AFTER_PLACE,
            }, additionalReleaseTime: {
                id: "additional_release_time",
                value: getParameterValue(parameterNames.blowOffClearanceTime),
                translationKey: "dashboard.additional_release_time",
                color: "#C71515",
                borderColor: "#C71515",
                parameterId: parameterNames.blowOffClearanceTime,
                group: PerformanceChartGroup.AFTER_PLACE,
            }, totalReleaseTime: {
                id: "total_release_time",
                value: activePerformanceData.blowOffTimeToAtm +
                    getParameterValue(parameterNames.blowOffClearanceTime),
                translationKey: "dashboard.total_release_time",
                descriptionKey: "descriptions.dashboard.total_release_time",
                color: "#FB7F7F",
                borderColor: "#C71515",
                group: PerformanceChartGroup.AFTER_PLACE,
            }, returnToPick: {
                id: "return_to_pick",
                // transferTimeReturn includes the next cycle's before-pick phase (vacuum + blow-off).
                // We subtract it here because the chart treats those phases as separate sequential bars.
                // This value must stay in sync with `beforePickOffset` in application-speed-box.tsx.
                value: activePerformanceData.transferTimeReturn -
                    getParameterValue(parameterNames.vacuumOnBeforePick) -
                    getParameterValue(parameterNames.blowOffOnBeforePick),
                translationKey: "dashboard.return_to_pick",
                descriptionKey: "descriptions.dashboard.return_to_pick",
                color: "#848484",
                borderColor: "#848484",
                group: PerformanceChartGroup.AFTER_PLACE,
            } }),
        targetPartsPerMinute: {
            value: getParameterValue(parameterNames.targetPartsPerMinute) || 1,
            translationKey: "dashboard.parts_per_minute",
        },
        targetSecsPerCycle: {
            value: 60 / targetPartsPerMinute.value,
            translationKey: "dashboard.sec_per_cycle",
        },
        totalTransferTime: {
            value: activePerformanceData.transferTimeReturn + transferTimeWithPart,
            translationKey: "dashboard.total_time",
        },
        airConsumption: {
            perCycle: {
                translationKey: "dashboard.nl_per_cycle",
                value: activePerformanceData.airConsumptionTotal,
            },
            perMinute: {
                translationKey: "dashboard.nl_per_minute",
                value: activePerformanceData.airConsumptionTotal *
                    targetPartsPerMinute.value,
            },
        },
        energyUsage: {
            total: {
                translationKey: "dashboard.energy_usage_total",
                value: activePerformanceData.energyUsageTotal,
                unit: Unit.WH,
            },
            BO: {
                translationKey: "dashboard.energy_usage_bo",
                value: activePerformanceData.energyUsageBO,
                unit: Unit.WH,
            },
            vacuumGeneration: {
                translationKey: "dashboard.energy_usage_vacuum_generation",
                value: activePerformanceData.energyUsageVacuumGeneration,
                unit: Unit.WH,
            },
            perDay: {
                translationKey: "dashboard.energy_usage_per_day",
                value: activePerformanceData.energyUsage8hr,
                unit: Unit.KWH,
            },
            compressorPower: {
                translationKey: "dashboard.energy_usage_compressor_usage",
                value: activePerformanceData.averageCompressorPower,
                unit: Unit.W,
            },
        },
        desiredVacuumLevels: __assign({ partSecured: {
                id: "part_secured_level",
                value: getParameterValue(parameterNames.partSecuredLevel),
                translationKey: "dashboard.part_secured_level",
                descriptionKey: "descriptions.dashboard.time_to_part_secured",
                color: partSecuredColor,
                borderColor: "#FFFFFF",
                group: PerformanceChartGroup.VACUUM_LEVELS,
                parameterId: parameterNames.partSecuredLevel,
            } }, (timeToEnergySave && {
            energySaving: {
                id: "final_vacuum_level",
                value: getParameterValue(parameterNames.energySavingLevel),
                translationKey: "dashboard.final_vacuum_level",
                descriptionKey: "descriptions.dashboard.final_vacuum_level",
                color: energySavingColor,
                borderColor: "#FFFFFF",
                group: PerformanceChartGroup.VACUUM_LEVELS,
                parameterId: parameterNames.energySavingLevel,
            },
        })),
    };
}
export function computeNonEsVacuumLevelDomain(nodeData) {
    var e_1, _a, e_2, _b;
    var _c;
    var pumps = getVacuumPumps(nodeData);
    var cups = getSuctionCups(nodeData);
    var allNodes = __spreadArray(__spreadArray([], __read(pumps), false), __read(cups), false);
    if (!allNodes.length)
        return undefined;
    var min = Number.MAX_SAFE_INTEGER;
    var max = Number.MIN_SAFE_INTEGER;
    try {
        for (var allNodes_1 = __values(allNodes), allNodes_1_1 = allNodes_1.next(); !allNodes_1_1.done; allNodes_1_1 = allNodes_1.next()) {
            var node = allNodes_1_1.value;
            try {
                for (var _d = (e_2 = void 0, __values((_c = node.performanceData.vacuumLevels) !== null && _c !== void 0 ? _c : [])), _e = _d.next(); !_e.done; _e = _d.next()) {
                    var vl = _e.value;
                    var converted = convertVacuumLevelToFixedNumber(vl);
                    min = Math.min(min, converted);
                    max = Math.max(max, converted);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_e && !_e.done && (_b = _d.return)) _b.call(_d);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (allNodes_1_1 && !allNodes_1_1.done && (_a = allNodes_1.return)) _a.call(allNodes_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    if (min === Number.MAX_SAFE_INTEGER)
        return undefined;
    return [min, max];
}
